import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getMediaUrl } from '../../services/api';
import GalleryGridEditor from '../GalleryGridEditor';
import useMediaFiltering from '../../hooks/useMediaFiltering';
import useCreditsTab from '../../hooks/useCreditsTab';
import useGalleryConfig from '../../hooks/useGalleryConfig';
import VisibilityToggle from './VisibilityToggle';
import './PopTemplate.css';

function PopTemplate({
  profileData,
  basicUser,
  isOwnProfile,
  onEdit,
  onTextClick,
  onColorClick,
  hiddenSections = [],
  onToggleSection,
  professions = [],
  onPhotoClick,
  galleryEditMode,
  onToggleGalleryEdit,
  onGalleryConfigChange,
  onGalleryReorder,
  galleryConfig,
  onGalleryPhotoCrop,
}) {
  const [activeSection, setActiveSection] = useState('home');

  const { settings, performer, credits, skills, media, links } = profileData;

  const accentColor = settings?.accent_color || '#C8A96E';
  const headingFont = settings?.heading_font || 'Space Grotesk';
  const bodyFont = settings?.body_font || 'Inter';
  const headingFontSize = settings?.heading_font_size || '86';
  const bodyFontSize = settings?.body_font_size || '16';

  const displayName =
    performer?.display_name || basicUser?.full_name || basicUser?.username || 'Unknown';
  const tagline = performer?.tagline || '';
  const bio = performer?.bio || basicUser?.bio || '';

  // Build subtitle: use tagline if set, otherwise professions
  const professionNames = professions.map((p) => p.profession);
  const subtitle =
    tagline ||
    (professionNames.length > 0
      ? professionNames.join(', ')
      : performer?.job_title || basicUser?.industry || '');

  // Shared hooks
  const { headshots, aboutPhotos, galleryPhotos, videos } = useMediaFiltering(media);
  const { galleryGridStyle } = useGalleryConfig(settings);
  const { activeCreditsTab, setActiveCreditsTab, creditCategories } = useCreditsTab(credits);

  const images =
    galleryPhotos.length > 0
      ? galleryPhotos
      : media
        ? media.filter((m) => m.media_type === 'image')
        : [];

  const navigateToSection = (sectionId) => {
    setActiveSection(sectionId);
  };

  // Split name for stacked display
  const nameParts = displayName.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  // Nav links
  const mediaMode = settings?.media_mode || 'together';
  const mediaNavLinks =
    mediaMode === 'separate'
      ? [
          { id: 'gallery', label: 'GALLERY', hidden: hiddenSections.includes('media') },
          { id: 'showreels', label: 'REELS', hidden: hiddenSections.includes('media') },
        ]
      : mediaMode === 'gallery_only'
        ? [{ id: 'gallery', label: 'GALLERY', hidden: hiddenSections.includes('media') }]
        : mediaMode === 'videos_only'
          ? [{ id: 'showreels', label: 'REELS', hidden: hiddenSections.includes('media') }]
          : [{ id: 'media', label: 'MEDIA', hidden: hiddenSections.includes('media') }];
  const navLinks = [
    { id: 'home', label: 'HOME', always: true },
    { id: 'about', label: 'ABOUT', hidden: hiddenSections.includes('about') },
    ...mediaNavLinks,
    {
      id: 'resume',
      label: 'RESUME',
      hidden: hiddenSections.includes('resume') || creditCategories.length === 0,
    },
    { id: 'contact', label: 'CONTACT', hidden: hiddenSections.includes('contact') },
  ].filter((l) => !l.hidden || onToggleSection);

  // Social icon renderer
  const renderSocialIcon = (linkType) => {
    switch (linkType) {
      case 'instagram':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        );
      case 'twitter':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        );
      case 'youtube':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
        );
      case 'vimeo':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z" />
          </svg>
        );
      case 'tiktok':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
          </svg>
        );
    }
  };

  return (
    <div
      className="pop-template"
      style={{
        '--accent-color': accentColor,
        '--heading-font': `'${headingFont}', sans-serif`,
        '--body-font': `'${bodyFont}', sans-serif`,
        '--heading-font-size': `${headingFontSize}px`,
        '--body-font-size': `${bodyFontSize}px`,
      }}
    >
      {/* Navigation */}
      <nav className="pop-nav">
        <div className="pop-nav-links">
          {navLinks.map((link) => (
            <button
              key={link.id}
              className={`${activeSection === link.id ? 'active' : ''}${link.hidden ? ' nav-link-hidden' : ''}`}
              onClick={() => navigateToSection(link.id)}
            >
              {link.label}
            </button>
          ))}
        </div>
        {isOwnProfile && (
          <button className="pop-nav-edit" onClick={onEdit}>
            Edit
          </button>
        )}
      </nav>

      {/* Hero Section */}
      {activeSection === 'home' && (
        <section
          className={`pop-hero${onColorClick ? ' showcase-editable-color' : ''}`}
          style={{ backgroundColor: accentColor }}
          onClick={
            onColorClick
              ? (e) => {
                  if (e.target === e.currentTarget || e.target.closest('.pop-hero-text')) {
                    e.stopPropagation();
                    onColorClick(e);
                  }
                }
              : undefined
          }
        >
          <div className="pop-hero-inner">
            <div className="pop-hero-photo">
              {headshots.length > 0 ? (
                <img
                  src={getMediaUrl(headshots[0].url)}
                  alt={displayName}
                  style={{
                    objectPosition: `${headshots[0].position_x ?? 50}% ${headshots[0].position_y ?? 50}%`,
                    transform:
                      (headshots[0].zoom ?? 100) > 100
                        ? `scale(${(headshots[0].zoom ?? 100) / 100})`
                        : undefined,
                    transformOrigin: `${headshots[0].position_x ?? 50}% ${headshots[0].position_y ?? 50}%`,
                  }}
                />
              ) : basicUser?.profile_image ? (
                <img src={basicUser.profile_image} alt={displayName} />
              ) : (
                <div className="pop-hero-photo-placeholder">
                  <span>{displayName.charAt(0).toUpperCase()}</span>
                </div>
              )}
              {isOwnProfile && onPhotoClick && (
                <div
                  className="photo-edit-overlay"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPhotoClick('headshot');
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <span>Change</span>
                </div>
              )}
            </div>
            <div className="pop-hero-text">
              <h1
                className={`pop-hero-name${onTextClick ? ' showcase-editable' : ''}`}
                onClick={
                  onTextClick
                    ? (e) => {
                        e.stopPropagation();
                        onTextClick('heading', e);
                      }
                    : undefined
                }
              >
                {firstName}
                {lastName && (
                  <>
                    <br />
                    {lastName}
                  </>
                )}
              </h1>
              {subtitle && (
                <p
                  className={`pop-hero-tagline${onTextClick ? ' showcase-editable' : ''}`}
                  onClick={
                    onTextClick
                      ? (e) => {
                          e.stopPropagation();
                          onTextClick('body', e);
                        }
                      : undefined
                  }
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Social bar */}
          {links && links.length > 0 && (
            <div className="pop-hero-social-bar">
              <div className="pop-hero-social-inner">
                {links.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.label || link.link_type}
                  >
                    {renderSocialIcon(link.link_type)}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Accent stripe */}
          <div className="pop-hero-stripe" />
        </section>
      )}

      {/* About */}
      {activeSection === 'about' && (!hiddenSections.includes('about') || onToggleSection) && (
        <section className="pop-about" style={{ backgroundColor: accentColor }}>
          <div className="pop-about-image">
            {aboutPhotos.length > 0 ? (
              <>
                <img
                  src={getMediaUrl(aboutPhotos[0].url)}
                  alt={displayName}
                  style={{
                    objectPosition: `${aboutPhotos[0].position_x ?? 50}% ${aboutPhotos[0].position_y ?? 50}%`,
                    transform:
                      (aboutPhotos[0].zoom ?? 100) > 100
                        ? `scale(${(aboutPhotos[0].zoom ?? 100) / 100})`
                        : undefined,
                    transformOrigin: `${aboutPhotos[0].position_x ?? 50}% ${aboutPhotos[0].position_y ?? 50}%`,
                  }}
                />
                {isOwnProfile && onPhotoClick && (
                  <div
                    className="photo-edit-overlay"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPhotoClick('about');
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <span>Change</span>
                  </div>
                )}
              </>
            ) : basicUser?.profile_image ? (
              <img src={basicUser.profile_image} alt={displayName} />
            ) : (
              <div className="pop-about-placeholder">{displayName.charAt(0).toUpperCase()}</div>
            )}
          </div>
          <div className="pop-about-content">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2
                className={`pop-section-title${onTextClick ? ' showcase-editable' : ''}`}
                onClick={
                  onTextClick
                    ? (e) => {
                        e.stopPropagation();
                        onTextClick('heading', e);
                      }
                    : undefined
                }
              >
                ABOUT
              </h2>
              <VisibilityToggle
                section="about"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            <div
              className={`pop-about-text${onTextClick ? ' showcase-editable' : ''}`}
              onClick={
                onTextClick
                  ? (e) => {
                      e.stopPropagation();
                      onTextClick('body', e);
                    }
                  : undefined
              }
            >
              {bio ? (
                bio.split('\n\n').map((paragraph, index) => <p key={index}>{paragraph}</p>)
              ) : (
                <p>Add your bio in the profile settings.</p>
              )}
            </div>
            {performer &&
              (performer.union_status || performer.representation || performer.height) && (
                <div className="pop-about-stats">
                  {performer.union_status && <span>{performer.union_status}</span>}
                  {performer.height && <span>{performer.height}</span>}
                  {performer.age_range && <span>Age Range: {performer.age_range}</span>}
                  {performer.representation && <span>Rep: {performer.representation}</span>}
                </div>
              )}
            {creditCategories.length > 0 && !hiddenSections.includes('resume') && (
              <button className="pop-btn" onClick={() => navigateToSection('resume')}>
                VIEW RESUME
              </button>
            )}
          </div>
        </section>
      )}

      {/* Separate: Showreels */}
      {activeSection === 'showreels' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section className="pop-section" style={{ backgroundColor: accentColor }}>
          <div className="pop-section-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="pop-section-title">REELS</h2>
              <VisibilityToggle
                section="media"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            {videos.length > 0 ? (
              <div className="pop-videos-grid">
                {videos.map((item, index) => (
                  <div key={index} className="pop-video-item">
                    {item.embed_url ? (
                      <div className="pop-video-wrap">
                        <iframe
                          src={item.embed_url}
                          title={item.title || 'Video'}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div className="pop-video-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                    {item.title && <p className="pop-video-title">{item.title}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '60px 40px', textAlign: 'center', color: '#999' }}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ marginBottom: '16px', opacity: 0.5 }}
                >
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
                <p style={{ fontSize: '16px', margin: 0 }}>
                  No videos added yet. Add videos in profile settings.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Separate: Gallery */}
      {activeSection === 'gallery' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section
          className="pop-section"
          style={{ backgroundColor: accentColor, position: 'relative' }}
        >
          <div className="pop-section-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="pop-section-title">GALLERY</h2>
              <VisibilityToggle
                section="media"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            {isOwnProfile && onToggleGalleryEdit && (
              <button
                className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
                onClick={onToggleGalleryEdit}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
              </button>
            )}
            {galleryEditMode && onGalleryConfigChange ? (
              <GalleryGridEditor
                photos={images}
                config={galleryConfig}
                onChange={onGalleryConfigChange}
                onReorder={onGalleryReorder}
                onCropPhoto={onGalleryPhotoCrop}
                theme="pop"
              />
            ) : (
              <div className="pop-gallery-grid" style={galleryGridStyle}>
                {images.length > 0 ? (
                  images.map((item, index) => (
                    <div key={index} className="pop-gallery-item">
                      <img
                        src={getMediaUrl(item.url)}
                        alt={item.title || 'Gallery image'}
                        style={{
                          objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                          transform:
                            (item.zoom ?? 100) > 100
                              ? `scale(${(item.zoom ?? 100) / 100})`
                              : undefined,
                          transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                        }}
                      />
                      {item.title && (
                        <div className="pop-gallery-overlay">
                          <span>{item.title}</span>
                        </div>
                      )}
                      {isOwnProfile && onGalleryPhotoCrop && (
                        <div
                          className="gallery-photo-crop-overlay"
                          onClick={(e) => onGalleryPhotoCrop(item, e)}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                            <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                          </svg>
                          <span>Crop</span>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="pop-empty">No photos added yet. Add them in profile settings.</p>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Together: Combined Media */}
      {activeSection === 'media' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section
          className="pop-section"
          style={{ backgroundColor: accentColor, position: 'relative' }}
        >
          <div className="pop-section-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="pop-section-title">MEDIA</h2>
              <VisibilityToggle
                section="media"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            {isOwnProfile && onToggleGalleryEdit && (
              <button
                className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
                onClick={onToggleGalleryEdit}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
              </button>
            )}
            {videos.length > 0 && (
              <div className="pop-videos-grid" style={{ marginBottom: '40px' }}>
                {videos.map((item, index) => (
                  <div key={index} className="pop-video-item">
                    {item.embed_url ? (
                      <div className="pop-video-wrap">
                        <iframe
                          src={item.embed_url}
                          title={item.title || 'Video'}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div className="pop-video-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                    {item.title && <p className="pop-video-title">{item.title}</p>}
                  </div>
                ))}
              </div>
            )}
            {galleryEditMode && onGalleryConfigChange ? (
              <GalleryGridEditor
                photos={images}
                config={galleryConfig}
                onChange={onGalleryConfigChange}
                onReorder={onGalleryReorder}
                onCropPhoto={onGalleryPhotoCrop}
                theme="pop"
              />
            ) : (
              <div className="pop-gallery-grid" style={galleryGridStyle}>
                {images.length > 0 ? (
                  images.map((item, index) => (
                    <div key={index} className="pop-gallery-item">
                      <img
                        src={getMediaUrl(item.url)}
                        alt={item.title || 'Gallery image'}
                        style={{
                          objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                          transform:
                            (item.zoom ?? 100) > 100
                              ? `scale(${(item.zoom ?? 100) / 100})`
                              : undefined,
                          transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                        }}
                      />
                      {item.title && (
                        <div className="pop-gallery-overlay">
                          <span>{item.title}</span>
                        </div>
                      )}
                      {isOwnProfile && onGalleryPhotoCrop && (
                        <div
                          className="gallery-photo-crop-overlay"
                          onClick={(e) => onGalleryPhotoCrop(item, e)}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                            <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                          </svg>
                          <span>Crop</span>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="pop-empty">No photos added yet. Add them in profile settings.</p>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Resume */}
      {activeSection === 'resume' &&
        (!hiddenSections.includes('resume') || onToggleSection) &&
        creditCategories.length > 0 && (
          <section className="pop-section" style={{ backgroundColor: accentColor }}>
            <div className="pop-section-inner">
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <h2 className="pop-section-title">RESUME</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {performer?.resume_pdf_url && (
                    <a
                      href={getMediaUrl(performer.resume_pdf_url)}
                      download
                      className="pop-resume-download"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download PDF
                    </a>
                  )}
                  {onToggleSection && (
                    <button
                      className={`section-visibility-toggle${hiddenSections.includes('resume') ? ' is-hidden' : ''}`}
                      onClick={() => onToggleSection('resume')}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {hiddenSections.includes('resume') ? (
                          <>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </>
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                      {hiddenSections.includes('resume') ? 'Hidden' : 'Visible'}
                    </button>
                  )}
                </div>
              </div>
              <div className="pop-credits-tabs">
                {creditCategories.map((category) => (
                  <button
                    key={category}
                    className={`pop-credits-tab${activeCreditsTab === category ? ' active' : ''}`}
                    onClick={() => setActiveCreditsTab(category)}
                    style={
                      activeCreditsTab === category
                        ? { borderColor: accentColor, color: accentColor }
                        : {}
                    }
                  >
                    {category}
                  </button>
                ))}
              </div>
              {activeCreditsTab && credits[activeCreditsTab] && (
                <table className="pop-credits-table">
                  <thead>
                    <tr>
                      <th>PRODUCTION</th>
                      <th>ROLE</th>
                      <th>COMPANY</th>
                      <th>DIRECTOR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {credits[activeCreditsTab].map((credit, index) => (
                      <tr key={index}>
                        <td>
                          <Link
                            to={`/production?name=${encodeURIComponent(credit.production)}${credit.company ? `&company=${encodeURIComponent(credit.company)}` : ''}`}
                            className="credit-production-link"
                          >
                            {credit.production}
                          </Link>
                        </td>
                        <td>{credit.role}</td>
                        <td>{credit.company || ''}</td>
                        <td>{credit.director || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}

      {/* Skills */}
      {activeSection === 'skills' &&
        (!hiddenSections.includes('skills') || onToggleSection) &&
        skills && (
          <section className="pop-section" style={{ backgroundColor: accentColor }}>
            <div className="pop-section-inner">
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <h2 className="pop-section-title">SKILLS</h2>
                {onToggleSection && (
                  <button
                    className={`section-visibility-toggle${hiddenSections.includes('skills') ? ' is-hidden' : ''}`}
                    onClick={() => onToggleSection('skills')}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {hiddenSections.includes('skills') ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                    {hiddenSections.includes('skills') ? 'Hidden' : 'Visible'}
                  </button>
                )}
              </div>
              <div className="pop-skills-grid">
                {(Array.isArray(skills)
                  ? [{ category: 'Skills', items: skills }]
                  : Object.entries(skills).map(([cat, items]) => ({ category: cat, items }))
                ).map(({ category, items }) => (
                  <div key={category} className="pop-skills-group">
                    <h3 className="pop-skills-category">{category}</h3>
                    <div className="pop-skills-list">
                      {items.map((skill, index) => (
                        <span key={index} className="pop-skill-tag">
                          {skill.skill || skill.name || skill}
                          {skill.proficiency && (
                            <span className="pop-skill-level"> ({skill.proficiency})</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

      {/* Contact */}
      {activeSection === 'contact' && (!hiddenSections.includes('contact') || onToggleSection) && (
        <section className="pop-section" style={{ backgroundColor: accentColor }}>
          <div className="pop-section-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="pop-section-title">GET IN TOUCH</h2>
              {onToggleSection && (
                <button
                  className={`section-visibility-toggle${hiddenSections.includes('contact') ? ' is-hidden' : ''}`}
                  onClick={() => onToggleSection('contact')}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {hiddenSections.includes('contact') ? (
                      <>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </>
                    ) : (
                      <>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </>
                    )}
                  </svg>
                  {hiddenSections.includes('contact') ? 'Hidden' : 'Visible'}
                </button>
              )}
            </div>
            {performer?.contact_blurb && (
              <p className="pop-contact-blurb">{performer.contact_blurb}</p>
            )}
            <div className="pop-contact-content">
              {performer?.representation && (
                <div className="pop-contact-item">
                  <span className="pop-contact-label">Representation</span>
                  <span className="pop-contact-value">{performer.representation}</span>
                </div>
              )}
              {basicUser?.location && (
                <div className="pop-contact-item">
                  <span className="pop-contact-label">Location</span>
                  <span className="pop-contact-value">{basicUser.location}</span>
                </div>
              )}
              {links &&
                links
                  .filter((l) => l.is_public !== 0)
                  .map((link, index) => (
                    <div key={index} className="pop-contact-item">
                      <span className="pop-contact-label">{link.label}</span>
                      <span className="pop-contact-value">
                        {link.url.startsWith('http') ? (
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pop-contact-link"
                          >
                            {link.url.replace(/^https?:\/\/(www\.)?/, '')}
                          </a>
                        ) : (
                          link.url
                        )}
                      </span>
                    </div>
                  ))}
              {links &&
                links.length === 0 &&
                !performer?.representation &&
                !basicUser?.location && (
                  <p
                    style={{
                      color: 'rgba(0,0,0,0.4)',
                      fontFamily: 'Rethink Sans',
                      fontSize: '14px',
                    }}
                  >
                    No contact info added yet
                  </p>
                )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default PopTemplate;
